/**
 * Cash/card checkout with an optional receipt email (#3348); legacy invoice/bank entries
 * remain available. With an invoice and Svea enabled, "payment link" creates
 * and sends a Svea link for the open balance instead of recording money (#2978).
 *
 * With SumUp terminals in use (#3500), a card goes to a terminal instead of a
 * typed reference: the dialog opens a terminal checkout and shows its live
 * state until it is recorded. "Enter the reference by hand" keeps the old
 * field as the fallback (PO decision A2). An open checkout is resumed when
 * the dialog is opened again.
 */

import { eurosToCents, formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  DialogContent,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import { FormDialog } from '@/components/common/FormDialog';
import { useNotify } from '@/hooks/useNotify';
import { useAuthStore } from '@/stores/authStore';
import { useSveaPaymentLinkStatus } from '@/hooks/useSveaIntegration';
import { useSumUpTerminalStatus } from '@/hooks/useSumUpIntegration';
import {
  defaultReaderId,
  refreshAfterTerminalCheckoutClosed,
  rememberReaderId,
  useOpenTerminalCheckout,
  useOpenTerminalCheckoutForInvoice,
  useStartTerminalCardPayment,
  type TerminalCheckout,
} from '@/hooks/useTerminalCheckout';
import { useQueryClient } from '@tanstack/react-query';
import { TerminalCheckoutPanel } from './TerminalCheckoutPanel';
import {
  printReceiptPdf,
  useCheckoutInvoice,
  useInvoiceDetail,
  useRecordPayment,
  useSendSveaPayment,
  type CheckoutInvoiceResponse,
  type CheckoutTender,
  type CheckoutWorkOrderData,
} from '@/hooks/useInvoices';
import { billingErrorCode, translateBillingError } from '@/lib/billingErrors';
import { DROPDOWN_MRU_SELECTORS, useDropdownMru } from '@/hooks/useDropdownMru';
import {
  isValidCardPaymentReference,
  normalizeCardPaymentReference,
} from '@/utils/cardPaymentReference';


function parseCents(value: string): number {
  return eurosToCents(value) ?? 0;
}

type PaymentMethod = 'cash' | 'card' | 'mixed' | 'invoice' | 'bank_transfer' | 'other' | 'svea_link';

type SveaChannel = 'email' | 'whatsapp';

/** Methods where the money came to the workshop's account against the invoice (#4113). */
function takesInvoiceReference(method: PaymentMethod): boolean {
  return method === 'invoice' || method === 'bank_transfer';
}

const PAYMENT_METHOD_OPTIONS: {
  value: PaymentMethod;
  labelKey: string;
  needsInvoice?: boolean;
  needsSvea?: boolean;
}[] = [
  { value: 'cash', labelKey: 'payment.methodCash' },
  { value: 'card', labelKey: 'payment.methodCard' },
  { value: 'mixed', labelKey: 'payment.methodMixed' },
  { value: 'invoice', labelKey: 'payment.methodInvoice', needsInvoice: true },
  { value: 'bank_transfer', labelKey: 'payment.methodBankTransfer', needsInvoice: true },
  // Gift card, insurer, credit from another invoice… The reference is required
  // so the ledger row says what the money was.
  { value: 'other', labelKey: 'payment.methodOther', needsInvoice: true },
  // Not money in hand: the link is created and sent now, the ledger row comes
  // when Svea confirms the payment (#2810). Needs the invoice for its open
  // balance and reference, and invoices.manage like the Svea routes do.
  { value: 'svea_link', labelKey: 'payment.methodSveaLink', needsInvoice: true, needsSvea: true },
];

interface RecordPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId?: string;
  workOrderId?: string;
  invoiceNumber?: string;
  totalCents: number;
  paidAmountCents: number;
  customerEmail?: string | null;
  initialMethod?: PaymentMethod;
  taxPercent?: number;
  /** Work-order checkout when no invoice exists yet (#1961). */
  checkoutWorkOrder?: {
    mutateAsync: (data: CheckoutWorkOrderData) => Promise<CheckoutInvoiceResponse>;
    isPending: boolean;
  };
}

export function RecordPaymentDialog({
  open,
  onClose,
  invoiceId,
  workOrderId,
  invoiceNumber,
  totalCents,
  paidAmountCents,
  customerEmail,
  initialMethod = 'card',
  taxPercent,
  checkoutWorkOrder,
}: RecordPaymentDialogProps) {
  const { t } = useTranslation('invoices');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const checkout = useCheckoutInvoice();
  const recordPayment = useRecordPayment();
  const sendSveaLink = useSendSveaPayment();
  const { data: invoiceDetail } = useInvoiceDetail(open && invoiceId ? invoiceId : undefined);
  const hasInvoice = Boolean(invoiceId);
  const canManageInvoices = useAuthStore((s) => s.hasPermission('invoices.manage'));
  const sveaStatus = useSveaPaymentLinkStatus(open && hasInvoice && canManageInvoices);
  const sveaAvailable = hasInvoice && canManageInvoices && sveaStatus.data?.enabled === true;
  const queryClient = useQueryClient();
  const terminalStatus = useSumUpTerminalStatus(open && hasInvoice);
  const terminalReaders = useMemo(() => terminalStatus.data?.readers ?? [], [terminalStatus.data?.readers]);
  const terminalAvailable = hasInvoice && terminalStatus.data?.enabled === true && terminalReaders.length > 0;
  const resumable = useOpenTerminalCheckoutForInvoice(invoiceId, open && terminalAvailable);
  const openTerminalCheckout = useOpenTerminalCheckout();
  const startTerminalCard = useStartTerminalCardPayment();

  const paymentMru = useDropdownMru(DROPDOWN_MRU_SELECTORS.paymentMethod);
  const remainingCents = totalCents - paidAmountCents;
  const defaultAmount = (remainingCents / 100).toFixed(2);
  const [amount, setAmount] = useState(defaultAmount);
  // An invoice-only method cannot be offered without an invoice, so the
  // work-order checkout falls back to card.
  const startMethod: PaymentMethod = !hasInvoice
    && PAYMENT_METHOD_OPTIONS.some((option) => option.value === initialMethod && option.needsInvoice)
    ? 'card'
    : initialMethod;
  const [method, setMethod] = useState<PaymentMethod>(startMethod);
  const [reference, setReference] = useState('');
  // The invoice's own reference number, prefilled into "Viite" for an
  // invoice / bank transfer payment (#4113). It is what the payer used.
  const invoiceReference = invoiceDetail?.paymentReferenceNumber ?? '';
  const [referenceBaseline, setReferenceBaseline] = useState('');
  const [receiptEmail, setReceiptEmail] = useState(customerEmail ?? '');
  const [cashAmount, setCashAmount] = useState('');
  const [cashReceived, setCashReceived] = useState(defaultAmount);
  // Payment value date for non-checkout methods (invoice/bank transfer), e.g. a
  // bank transfer that arrived on a different day than it is keyed in (#1038).
  const [paymentDate, setPaymentDate] = useState('');
  const [sveaChannel, setSveaChannel] = useState<SveaChannel>('email');
  const [sveaPhone, setSveaPhone] = useState('');
  // A2 fallback: type the terminal reference instead of using the terminal.
  const [manualReference, setManualReference] = useState(false);
  const [readerId, setReaderId] = useState('');
  const [activeCheckoutId, setActiveCheckoutId] = useState<string | null>(null);
  const idempotencyRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const prefilledFromInvoice = useRef(false);
  const prefilledReference = useRef(false);

  useEffect(() => {
    if (!open) return;
    setAmount(defaultAmount);
    setMethod(startMethod);
    setReference('');
    setReferenceBaseline('');
    setCashAmount('');
    setCashReceived(defaultAmount);
    setReceiptEmail(customerEmail ?? '');
    setPaymentDate('');
    setSveaChannel('email');
    setSveaPhone('');
    setManualReference(false);
    setActiveCheckoutId(null);
    idempotencyRef.current = null;
    prefilledFromInvoice.current = false;
    prefilledReference.current = false;
  }, [open, invoiceId, defaultAmount, customerEmail, startMethod]);

  // The terminal chosen at this till last time, while it is still paired.
  useEffect(() => {
    if (!open || terminalReaders.length === 0) return;
    setReaderId((current) => defaultReaderId(terminalReaders, current));
  }, [open, terminalReaders]);

  // A checkout left open (dialog closed, page reloaded) is resumed, not
  // duplicated — but only from a fetch made now: a cached answer may name a
  // checkout that has finished since.
  useEffect(() => {
    const resumed = resumable.data;
    if (open && resumable.isFetchedAfterMount && resumed?.status === 'open') setActiveCheckoutId(resumed.id);
  }, [open, resumable.data, resumable.isFetchedAfterMount]);

  // Prefill from the invoice once it loads, but only once: the field is
  // optional (#3348), so a cleared address must stay cleared.
  useEffect(() => {
    const invoiceEmail = invoiceDetail?.customerEmail;
    if (!open || prefilledFromInvoice.current || !invoiceEmail) return;
    prefilledFromInvoice.current = true;
    setReceiptEmail((current) => (current === '' ? invoiceEmail : current));
  }, [open, invoiceDetail?.customerEmail]);

  // Same once-only rule for the reference: a cleared "Viite" stays cleared.
  useEffect(() => {
    if (!open || prefilledReference.current || !invoiceReference || !takesInvoiceReference(method)) return;
    prefilledReference.current = true;
    setReference((current) => (current === '' ? invoiceReference : current));
    if (method === startMethod) setReferenceBaseline(invoiceReference);
  }, [open, invoiceReference, method, startMethod]);

  const amountCents = parseCents(amount);
  const cashAmountCents = method === 'mixed' ? parseCents(cashAmount) : amountCents;
  const cardAmountCents = method === 'mixed' ? amountCents - cashAmountCents : amountCents;
  const cashReceivedCents = parseCents(cashReceived);
  const isCheckout = method === 'cash' || method === 'card' || method === 'mixed';
  const isSveaLink = method === 'svea_link';
  const receiptEmailTrimmed = receiptEmail.trim();
  const emailValid = /^\S+@\S+\.\S+$/.test(receiptEmailTrimmed);
  // Optional since #3348: most counter customers want the receipt on paper,
  // so an empty field means "do not email", not "cannot pay".
  const receiptEmailOk = receiptEmailTrimmed === '' || emailValid;
  const amountValid = amountCents > 0 && amountCents <= remainingCents;
  const cashValid = (method !== 'cash' && method !== 'mixed')
    || (cashAmountCents > 0 && cashReceivedCents >= cashAmountCents);
  const mixedValid = method !== 'mixed' || (cashAmountCents > 0 && cardAmountCents > 0);
  const cardReferenceNormalized = normalizeCardPaymentReference(reference);
  const terminalMode = terminalAvailable && !manualReference && (method === 'card' || method === 'mixed');
  const cardValid = (method !== 'card' && method !== 'mixed')
    || (terminalMode ? readerId !== '' : isValidCardPaymentReference(cardReferenceNormalized));
  // The link is always for the whole open balance — Svea reads it from the
  // ledger — so the amount field does not apply. WhatsApp may leave the number
  // empty; the server then uses the payer's own number.
  const sveaValid = sveaAvailable && remainingCents > 0
    && (sveaChannel === 'whatsapp' || emailValid);
  const otherValid = method !== 'other' || reference.trim() !== '';
  const formValid = isSveaLink
    ? sveaValid
    : amountValid && otherValid && (!isCheckout || (receiptEmailOk && cashValid && mixedValid && cardValid));
  const isPending = checkout.isPending
    || openTerminalCheckout.isPending
    || startTerminalCard.isPending
    || recordPayment.isPending
    || sendSveaLink.isPending
    || (checkoutWorkOrder?.isPending ?? false);
  const isDirty = amount !== defaultAmount
    || method !== startMethod
    || reference !== referenceBaseline
    || receiptEmail !== (customerEmail ?? invoiceDetail?.customerEmail ?? '')
    || cashAmount !== ''
    || cashReceived !== defaultAmount;

  const tenders = useMemo<CheckoutTender[]>(() => {
    if (method === 'cash') {
      return [{ method: 'cash', amountCents, cashReceivedCents }];
    }
    if (method === 'card') {
      return [{
        method: 'card',
        amountCents,
        paymentReference: normalizeCardPaymentReference(reference),
      }];
    }
    if (method === 'mixed') {
      return [
        { method: 'cash', amountCents: cashAmountCents, cashReceivedCents },
        {
          method: 'card',
          amountCents: cardAmountCents,
          paymentReference: normalizeCardPaymentReference(reference),
        },
      ];
    }
    return [];
  }, [amountCents, cardAmountCents, cashAmountCents, cashReceivedCents, method, reference]);

  const printReceipt = useCallback(async (receiptId: string): Promise<void> => {
    try {
      const printed = await printReceiptPdf(receiptId);
      if (printed.status === 'left-open') notify.info(tc('print.pdfUseBrowserDialog'));
    } catch (error) {
      notify.error(t('payments.openReceiptFailed'), translateBillingError(error, t));
    }
  }, [notify, t, tc]);

  const notifyReceipt = useCallback((receipt: { id: string; receiptNumber: string; recipientEmail: string | null }) => {
    notify.success(
      t('payment.receiptSuccess'),
      receipt.recipientEmail
        ? t('payment.receiptSuccessDesc', { receiptNumber: receipt.receiptNumber, email: receipt.recipientEmail })
        : t('payment.receiptSuccessNoEmailDesc', { receiptNumber: receipt.receiptNumber }),
      // Paper first (#3348): keep the print action up long enough to reach for it.
      {
        action: { label: t('actions.printReceipt'), onClick: () => void printReceipt(receipt.id) },
        duration: 15000,
      },
    );
  }, [notify, printReceipt, t]);

  const handleTerminalFinished = useCallback((finished: TerminalCheckout) => {
    // Recorded by a webhook or the sweep as well as by this till: refresh everything.
    refreshAfterTerminalCheckoutClosed(queryClient, finished.invoiceId);
    if (finished.status === 'abandoned') {
      notify.info(t('terminal.abandoned'));
      setActiveCheckoutId(null);
      // A new attempt is a new checkout, not a replay of the abandoned one.
      idempotencyRef.current = null;
      return;
    }
    if (finished.status === 'record_failed') {
      // Cards were charged but not recorded: keep the panel open so the till sees it.
      notify.error(t('terminal.actionFailed'), t('terminal.recordFailed'));
      return;
    }
    if (finished.receipt) notifyReceipt(finished.receipt);
    onClose();
  }, [notify, notifyReceipt, onClose, queryClient, t]);

  const startTerminalCheckout = async (): Promise<void> => {
    if (!invoiceId) return;
    const cash = method === 'mixed' ? cashAmountCents : 0;
    const fingerprint = JSON.stringify({ terminal: true, receiptEmail: receiptEmailTrimmed, cash, cashReceivedCents });
    if (idempotencyRef.current?.fingerprint !== fingerprint) {
      idempotencyRef.current = { fingerprint, key: crypto.randomUUID() };
    }
    rememberReaderId(readerId);
    let opened: TerminalCheckout;
    try {
      opened = await openTerminalCheckout.mutateAsync({
        invoiceId,
        cashAmountCents: cash,
        cashReceivedCents: method === 'mixed' ? cashReceivedCents : 0,
        ...(receiptEmailTrimmed ? { receiptEmail: receiptEmailTrimmed } : {}),
        idempotencyKey: idempotencyRef.current.key,
      });
    } catch (error) {
      // Another till or tab opened one meanwhile: show that one instead of an error.
      if (billingErrorCode(error) === 'terminal_checkout_open') {
        const existing = (await resumable.refetch()).data;
        if (existing?.status === 'open') {
          setActiveCheckoutId(existing.id);
          return;
        }
      }
      throw error;
    }
    setActiveCheckoutId(opened.id);
    await startTerminalCard.mutateAsync({ checkoutId: opened.id, readerId, amountCents: cardAmountCents });
  };

  const handleSubmit = async (): Promise<void> => {
    if (!formValid) return;
    try {
      if (terminalMode) {
        await startTerminalCheckout();
        return;
      }
      if (isSveaLink) {
        if (!invoiceId) return;
        const recipient = sveaChannel === 'email' ? receiptEmail.trim() : sveaPhone.trim();
        await sendSveaLink.mutateAsync({
          invoiceId,
          channel: sveaChannel,
          ...(recipient ? { recipient } : {}),
        });
        notify.success(t('payment.sveaLinkSent'), t('payment.sveaLinkSentDesc'));
        onClose();
        return;
      }
      if (isCheckout) {
        const fingerprint = JSON.stringify({ receiptEmail: receiptEmailTrimmed, tenders });
        if (idempotencyRef.current?.fingerprint !== fingerprint) {
          idempotencyRef.current = { fingerprint, key: crypto.randomUUID() };
        }
        const result = invoiceId
          ? await checkout.mutateAsync({
              invoiceId,
              workOrderId,
              ...(receiptEmailTrimmed ? { receiptEmail: receiptEmailTrimmed } : {}),
              tenders,
              idempotencyKey: idempotencyRef.current.key,
            })
          : checkoutWorkOrder && workOrderId
            ? await checkoutWorkOrder.mutateAsync({
                workOrderId,
                ...(receiptEmailTrimmed ? { receiptEmail: receiptEmailTrimmed } : {}),
                tenders,
                idempotencyKey: idempotencyRef.current.key,
                ...(taxPercent != null ? { taxPercent } : {}),
              })
            : null;
        if (!result) return;
        notifyReceipt(result.receipt);
      } else {
        if (!invoiceId) return;
        await recordPayment.mutateAsync({
          invoiceId,
          workOrderId,
          amountCents,
          paymentMethod: method,
          paymentReference: reference.trim() || undefined,
          paymentDate: paymentDate ? new Date(paymentDate).toISOString() : undefined,
        });
        notify.success(t('payment.success'), t('payment.successDesc', { amount }));
      }
      idempotencyRef.current = null;
      onClose();
    } catch (error) {
      notify.error(t('payment.failedTitle'), translateBillingError(error, t));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('payment.title')}
      isDirty={isDirty}
      headerIcon={<PaymentIcon />}
      onSubmit={() => void handleSubmit()}
      submitLabel={isSveaLink
        ? t('payment.sveaLinkSubmit')
        : terminalMode ? t('terminal.sendToTerminal')
        : isCheckout ? t('payment.checkoutSubmit') : t('payment.submit')}
      submitDisabled={!formValid}
      isLoading={isPending}
      maxWidth="sm"
      {...(activeCheckoutId !== null
        ? {
            // The checkout lives on the server; closing the dialog leaves it
            // running and it is resumed on the next open.
            renderActions: ({ onClose: close }: { onClose: () => void }) => (
              <Button onClick={close}>{tc('close')}</Button>
            ),
          }
        : {})}
    >
      <DialogContent>
        {activeCheckoutId !== null ? (
          <TerminalCheckoutPanel
            key={activeCheckoutId}
            checkoutId={activeCheckoutId}
            readers={terminalReaders}
            initialReaderId={readerId}
            onFinished={handleTerminalFinished}
          />
        ) : (
        <>
        <Box sx={{ mb: 2.5, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          {invoiceNumber ? (
            <Typography variant="body2" color="text.secondary">{invoiceNumber}</Typography>
          ) : null}
          <Stack direction="row" justifyContent="space-between" mt={1}>
            <Typography variant="body2">{t('detail.total')}</Typography>
            <Typography variant="body2" fontWeight={600}>{formatPrice(totalCents)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">{t('detail.paidAmount')}</Typography>
            <Typography variant="body2">{formatPrice(paidAmountCents)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" fontWeight={600}>{t('payment.remaining')}</Typography>
            <Typography variant="body2" fontWeight={600} color="error.main">
              {formatPrice(remainingCents)}
            </Typography>
          </Stack>
        </Box>

        <Stack spacing={2.25}>
          {!isSveaLink ? (
          <TextField
            type="number"
            label={t('payment.amount')}
            value={amount}
            onChange={(event) => {
              setAmount(event.target.value);
              if (method === 'cash') setCashReceived(event.target.value);
            }}
            size="small"
            error={amountCents > remainingCents}
            helperText={amountCents > remainingCents ? t('payment.amountExceedsRemaining') : undefined}
            slotProps={{ htmlInput: { min: 0.01, max: remainingCents / 100, step: '0.01' } }}
          />
          ) : null}
          <FormControl size="small">
            <InputLabel>{t('payment.method')}</InputLabel>
            <Select
              value={method}
              label={t('payment.method')}
              onChange={(event) => {
                const nextMethod = event.target.value as PaymentMethod;
                // The invoice reference is not a card terminal reference: do
                // not carry it into the card field, and bring it back when
                // switching to another invoice / bank transfer method.
                if (invoiceReference && takesInvoiceReference(nextMethod) !== takesInvoiceReference(method)) {
                  if (takesInvoiceReference(nextMethod) && reference === '') setReference(invoiceReference);
                  else if (!takesInvoiceReference(nextMethod) && reference === invoiceReference) setReference('');
                }
                setMethod(nextMethod);
                paymentMru.record(nextMethod);
                if (nextMethod === 'cash') setCashReceived(amount);
              }}
            >
              {paymentMru.order(
                PAYMENT_METHOD_OPTIONS.filter((option) =>
                  (hasInvoice || !option.needsInvoice) && (sveaAvailable || !option.needsSvea)),
                (option) => option.value,
              ).map((option) => (
                <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {method === 'mixed' ? (
            <TextField
              type="number"
              label={t('payment.cashAmount')}
              value={cashAmount}
              onChange={(event) => {
                setCashAmount(event.target.value);
                setCashReceived(event.target.value);
              }}
              size="small"
              helperText={t('payment.cardAmount', { amount: formatPrice(Math.max(0, cardAmountCents)) })}
              slotProps={{ htmlInput: { min: 0.01, max: Math.max(0, (amountCents - 1) / 100), step: '0.01' } }}
            />
          ) : null}

          {(method === 'cash' || method === 'mixed') ? (
            <TextField
              type="number"
              label={t('payment.cashReceived')}
              value={cashReceived}
              onChange={(event) => setCashReceived(event.target.value)}
              size="small"
              error={cashReceivedCents > 0 && cashReceivedCents < cashAmountCents}
              helperText={t('payment.change', {
                amount: formatPrice(Math.max(0, cashReceivedCents - cashAmountCents)),
              })}
              slotProps={{ htmlInput: { min: cashAmountCents / 100, step: '0.01' } }}
            />
          ) : null}

          {terminalMode ? (
            <Stack spacing={0.5}>
              <FormControl size="small">
                <InputLabel id="payment-terminal-label">{t('terminal.reader')}</InputLabel>
                <Select
                  labelId="payment-terminal-label"
                  label={t('terminal.reader')}
                  value={readerId}
                  onChange={(event) => setReaderId(event.target.value)}
                >
                  {terminalReaders.map((reader) => (
                    <MenuItem key={reader.id} value={reader.id}>{reader.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                {t('terminal.sendsAmount', { amount: formatPrice(Math.max(0, cardAmountCents)) })}
              </Typography>
              <Link component="button" type="button" variant="body2" sx={{ alignSelf: 'flex-start' }} onClick={() => setManualReference(true)}>
                {t('terminal.enterReferenceManually')}
              </Link>
            </Stack>
          ) : (method === 'card' || method === 'mixed') ? (
            <Stack spacing={0.5}>
              <TextField
                required
                label={t('payment.cardReference')}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                size="small"
                error={reference.trim() !== '' && !cardValid}
                helperText={
                  reference.trim() !== '' && !cardValid
                    ? t('payment.cardReferenceInvalid')
                    : t('payment.cardReferenceHelp')
                }
              />
              {terminalAvailable ? (
                <Link component="button" type="button" variant="body2" sx={{ alignSelf: 'flex-start' }} onClick={() => setManualReference(false)}>
                  {t('terminal.backToTerminal')}
                </Link>
              ) : null}
            </Stack>
          ) : null}

          {isSveaLink ? (
            <>
              <Typography variant="body2">
                {t('payment.sveaLinkAmount', { amount: formatPrice(remainingCents) })}
              </Typography>
              <RadioGroup
                row
                value={sveaChannel}
                onChange={(event) => setSveaChannel(event.target.value as SveaChannel)}
                aria-label={t('payment.sveaLinkChannel')}
              >
                <FormControlLabel value="email" control={<Radio size="small" />} label={t('payment.sveaLinkChannelEmail')} />
                <FormControlLabel value="whatsapp" control={<Radio size="small" />} label={t('payment.sveaLinkChannelWhatsapp')} />
              </RadioGroup>
              {sveaChannel === 'email' ? (
                <TextField
                  required
                  type="email"
                  label={t('payment.sveaLinkEmail')}
                  value={receiptEmail}
                  onChange={(event) => setReceiptEmail(event.target.value)}
                  size="small"
                  error={receiptEmail !== '' && !emailValid}
                />
              ) : (
                <TextField
                  type="tel"
                  label={t('payment.sveaLinkPhone')}
                  value={sveaPhone}
                  onChange={(event) => setSveaPhone(event.target.value)}
                  size="small"
                  helperText={t('payment.sveaLinkPhoneHelp')}
                />
              )}
              <Alert severity="info">{t('payment.sveaLinkNotice')}</Alert>
            </>
          ) : isCheckout ? (
            <>
              <TextField
                type="email"
                label={t('payment.receiptEmail')}
                value={receiptEmail}
                onChange={(event) => setReceiptEmail(event.target.value)}
                size="small"
                error={receiptEmail !== '' && !emailValid}
                helperText={t('payment.receiptEmailHelp')}
              />
              <Alert severity="info">
                {receiptEmailTrimmed ? t('payment.automaticEmailNotice') : t('payment.paperReceiptNotice')}
              </Alert>
            </>
          ) : (
            <>
              <TextField
                required={method === 'other'}
                label={method === 'other' ? t('payment.otherReference') : t('payment.reference')}
                value={reference}
                onChange={(event) => setReference(event.target.value)}
                size="small"
                {...(method === 'other' ? { helperText: t('payment.otherReferenceHelp') } : {})}
              />
              <TextField
                type="date"
                label={t('payment.paymentDate')}
                value={paymentDate}
                onChange={(event) => setPaymentDate(event.target.value)}
                size="small"
                helperText={t('payment.paymentDateHelp')}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </>
          )}
        </Stack>
        </>
        )}
      </DialogContent>
    </FormDialog>
  );
}
